import { Injectable } from "@angular/core";
import { NgRedux } from "@angular-redux/store";
import * as signalR from "@microsoft/signalr";

import { ActionKeys } from "../../pb-screen/action.keys";
import { IAppState, IConnectionStatus } from "../../store/IAppState";

@Injectable({
  providedIn: "root"
})
export class SignalrConnectionStatusService {
  constructor(private readonly ngRedux: NgRedux<IAppState>) {
  }

  public onReconnecting(): void {
    this.updateStatus({
      state: signalR.HubConnectionState.Reconnecting,
      isDataStale: true,
      isIncidentAcknowledged: false
    });
  }

  public onTransientReconnected(channelSubscriptionsSucceeded: boolean): void {
    const currentStatus = this.getCurrentStatus();
    const canResume = currentStatus.isIncidentAcknowledged ||
      (channelSubscriptionsSucceeded && this.isTransientAutoCloseEnabled());

    this.updateStatus({
      state: signalR.HubConnectionState.Connected,
      isDataStale: !canResume,
      // A successful transport reconnect completes the current incident. Keeping
      // the status non-stale is enough to keep an acknowledged warning hidden.
      isIncidentAcknowledged: false
    });
  }

  public onConnectionClosed(): void {
    const isAcknowledged = this.getCurrentStatus().isIncidentAcknowledged;

    this.updateStatus({
      state: signalR.HubConnectionState.Disconnected,
      isDataStale: !isAcknowledged,
      isIncidentAcknowledged: isAcknowledged
    });
  }

  public onStartSucceeded(isFirstConnection: boolean): void {
    const isAcknowledged = this.getCurrentStatus().isIncidentAcknowledged;

    this.updateStatus({
      state: signalR.HubConnectionState.Connected,
      isDataStale: !isFirstConnection && !isAcknowledged,
      // Starting (or restarting) successfully completes the incident.
      isIncidentAcknowledged: false
    });
  }

  public onStartFailed(isFirstConnection: boolean): void {
    const isAcknowledged = this.getCurrentStatus().isIncidentAcknowledged;

    this.updateStatus({
      state: signalR.HubConnectionState.Disconnected,
      isDataStale: !isFirstConnection && !isAcknowledged,
      isIncidentAcknowledged: isAcknowledged
    });
  }

  public acknowledgeCurrentIncident(status?: IConnectionStatus): void {
    const currentStatus = status || this.getCurrentStatus();

    this.updateStatus({
      state: currentStatus.state,
      isDataStale: false,
      isIncidentAcknowledged: true
    });
  }

  private isTransientAutoCloseEnabled(): boolean {
    const state = this.ngRedux.getState();
    return !!state &&
      !!state.applicationUser &&
      !!state.applicationUser.generalSetting &&
      state.applicationUser.generalSetting.autoCloseSignalRConnectionWarning === true;
  }

  private getCurrentStatus(): IConnectionStatus {
    const state = this.ngRedux.getState();
    if (state && state.connectionStatus) {
      return state.connectionStatus;
    }

    return {
      state: signalR.HubConnectionState.Connecting,
      isDataStale: false,
      isIncidentAcknowledged: false
    };
  }

  private updateStatus(connectionStatus: IConnectionStatus): void {
    this.ngRedux.dispatch({
      type: ActionKeys.UPDATE_CONNECTION_STATUS,
      connectionStatus
    });
  }
}
